/**
 * announcement-bar.js — Fixed & Enhanced
 *
 * Fixes:
 *  • Dots are now optional — only built when data-show-dots="true"
 *  • Close uses CSS animation class, no inline script needed
 *  • Marquee pauses on hover/focus for a11y
 */

if (!customElements.get("m-announcement-bar")) {
  class MAnnouncementBar extends HTMLElement {
    connectedCallback() {
      const enableSlider  = this.dataset.enableSlider  === "true";
      const mobileScroll  = this.dataset.mobileScroll  === "true";
      const desktopScroll = this.dataset.desktopScroll === "true";
      const showDots      = this.dataset.showDots       === "true";

      this._initClose();
      if (enableSlider)  this.initSlider(showDots);
      if (mobileScroll)  this._initMarquee(".m-announcement-bar__mobile-view .m-announcement-bar__marquee-track");
      if (desktopScroll) this._initMarquee(".m-announcement-bar__desktop-marquee-view .m-announcement-bar__marquee-track");
    }

    /* ── Animated close ─────────────────────────────────────────────────── */
    _initClose() {
      const bar  = this.closest(".m-announcement-bar");
      const btn  = bar && bar.querySelector(".m-announcement-bar__close");
      if (!btn) return;

      btn.addEventListener("click", () => {
        bar.classList.add("m-announcement-bar--closing");
        bar.addEventListener("animationend", () => {
          bar.remove();
          document.documentElement.style.setProperty("--m-announcement-height", "0px");
        }, { once: true });
      });
    }

    /* ── Swiper slider ──────────────────────────────────────────────────── */
    initSlider(showDots) {
      const autoplay       = this.dataset.enableAutoplay === "true";
      const autoplaySpeed  = this.dataset.autoplaySpeed;
      const slideContainer = this.querySelector(".swiper-container");
      if (!slideContainer) return;

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView:  1,
        slidesPerGroup: 1,
        autoplay: autoplay
          ? { delay: parseInt(autoplaySpeed, 10) * 1000, disableOnInteraction: false }
          : false,
        loop: true,
        navigation: {
          nextEl: this.querySelector(".swiper-button-next"),
          prevEl: this.querySelector(".swiper-button-prev"),
        },
        on: {
          slideChange: (swiper) => {
            if (showDots) this._syncDots(swiper.realIndex);
          },
        },
      });

      if (showDots) this._buildDots(slideContainer);
    }

    /* ── Dots ───────────────────────────────────────────────────────────── */
    _buildDots(slideContainer) {
      const slides = slideContainer.querySelectorAll(
        ".swiper-slide:not(.swiper-slide-duplicate)"
      );
      if (slides.length <= 1) return;

      const wrap = document.createElement("div");
      wrap.className = "m-announcement-bar__dots";
      wrap.setAttribute("role", "tablist");
      wrap.setAttribute("aria-label", "Announcement slides");

      slides.forEach((_, i) => {
        const dot = document.createElement("button");
        dot.className  = "m-announcement-bar__dot" + (i === 0 ? " is-active" : "");
        dot.setAttribute("role", "tab");
        dot.setAttribute("aria-label", `Slide ${i + 1}`);
        dot.setAttribute("aria-selected", i === 0 ? "true" : "false");
        dot.addEventListener("click", () => this.slider && this.slider.slideToLoop(i));
        wrap.appendChild(dot);
      });

      slideContainer.parentNode.insertBefore(wrap, slideContainer.nextSibling);
      this._dots = wrap.querySelectorAll(".m-announcement-bar__dot");
    }

    _syncDots(realIndex) {
      if (!this._dots) return;
      this._dots.forEach((dot, i) => {
        const active = i === realIndex;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-selected", active ? "true" : "false");
      });
    }

    /* ── Marquee — pause on hover / focus ───────────────────────────────── */
    _initMarquee(trackSelector) {
      const bar   = this.closest(".m-announcement-bar");
      const track = this.querySelector(trackSelector);
      if (!bar || !track) return;

      const pause  = () => (track.style.animationPlayState = "paused");
      const resume = () => (track.style.animationPlayState = "running");

      bar.addEventListener("mouseenter", pause);
      bar.addEventListener("mouseleave", resume);
      bar.addEventListener("focusin",    pause);
      bar.addEventListener("focusout",   resume);
    }
  }

  customElements.define("m-announcement-bar", MAnnouncementBar);
}